import { Box, Theme, SxProps } from '@mui/material';
import { FC, PropsWithChildren, useMemo } from 'react';

import { useScheduleGridContext } from './ScheduleGridContext';
import { getGridBodyHeight, getMinutesSinceMidnight } from './utils';
import { SystemStyleObject } from '@mui/system';

export interface ScheduleGridDayColumnProps extends PropsWithChildren {
  sx?: SxProps<Theme>;
}

export const ScheduleGridDayColumn: FC<ScheduleGridDayColumnProps> = ({ children, sx }) => {
  const { hourHeight, gridLinesPerHour, timeRange } = useScheduleGridContext();

  const { totalHeight, gridLinePositions } = useMemo(() => {
    const height = getGridBodyHeight(timeRange, hourHeight);
    const startMinutes = getMinutesSinceMidnight(timeRange.start);
    const endMinutes = getMinutesSinceMidnight(timeRange.end);
    const totalHours = (endMinutes - startMinutes) / 60;

    const lineInterval = hourHeight / gridLinesPerHour;
    const lineCount = Math.floor(totalHours * gridLinesPerHour);
    const positions = Array.from({ length: lineCount }, (_, i) => (i + 1) * lineInterval);

    return { totalHeight: height, gridLinePositions: positions };
  }, [timeRange, hourHeight, gridLinesPerHour]);

  return (
    <Box
      sx={(theme) => ({
        height: totalHeight,
        flex: 1,
        position: 'relative',
        borderRight: theme.mixins.borderValue(),
        ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
      })}
    >
      {gridLinePositions.map((top, index) => (
        <Box
          key={index}
          sx={(theme) => ({
            top,
            position: 'absolute',
            left: 0,
            right: 0,
            borderBottom: theme.mixins.borderValue(),
            pointerEvents: 'none',
          })}
        />
      ))}
      {children}
    </Box>
  );
};
